import dayjs from "dayjs";
import { EyeIcon } from "lucide-react";
import { useMemo } from "react";

import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { getApiUrl } from "@/lib/api";
import type { OrgDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function ClientInvoicesTable({ org }: { org: OrgDetail }) {
  const invoices = useMemo(() => {
    const invoices = [...org.invoices];
    invoices.unshift({
      id: 0,
      createdAt: new Date(),
      filePath: "-",
      orgId: org.id,
      billingRunId: null,
      downloadKey: null,
    });
    return invoices;
  }, [org.id, org.invoices]);

  const { options } = useAdminGridOptions(
    invoices,
    [
      {
        field: "id",
        headerName: "Invoice ID",
        cellRendererParams: (params) => ({
          id: params.data.id,
          filePath: params.data.filePath,
        }),
        cellRenderer: (params) => {
          const id = params.id;
          const isPreview = id === 0;
          const filePath = params.filePath;
          const name = isPreview ? (
            <span className="inline-flex gap-2 items-center">
              <EyeIcon className="size-4" /> Preview next invoice
            </span>
          ) : (
            `Invoice #${params.id}`
          );
          const apiPath = isPreview
            ? `/api/invoice/preview/${org.id}`
            : `/api/data/invoices/${filePath}`;
          return filePath ? (
            // biome-ignore lint/suspicious/noExplicitAny: doesn't like external links
            <Link href={{ to: getApiUrl(apiPath) as any }} target="_blank">
              {name}
            </Link>
          ) : (
            name
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Date",
        cellRendererParams: (params) => ({
          id: params.data.id,
          createdAt: params.data.createdAt,
        }),
        cellRenderer: (params) => {
          const id = params.id;
          const isPreview = id === 0;
          return (
            <Text mono size="1" muted>
              {isPreview
                ? "-"
                : dayjs(params.createdAt).format("MM/DD/YYYY HH:mm z")}
            </Text>
          );
        },
      },
    ],
    { sideBar: undefined },
  );

  return (
    <TabLayout>
      <AdminGrid {...options} />
    </TabLayout>
  );
}
